import React, { useEffect, useState } from "react";
import { useFormikContext, ErrorMessage } from "formik";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { dspDropdownOptions } from "../../Data/dropdownOptions";
import MultiSelect from "../MultiSelect/MultiSelect";
import ArtistTypeahead from "../../../components/roster/ArtistTypeahead";
import { useLocation } from "react-router-dom";
import { PitchFormValues } from "../Schema/pitchValidationSchema";
import { artistsApi } from "../../../repositories/artists";
import Swal from "sweetalert2";
import { RequiredAsterisk } from "../../../components/common/RequiredAsterisk";
import InfoTooltip from "../../../components/common/InfoTooltip";
import CharacterCounterDisplay from "../../../components/common/CharacterCounterDisplay";
import UploadModal from "../../../components/modal/UploadModal";
import { ArrowUpTrayIcon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { ImageWithArtistId } from "../../types/form";
interface LocationState {
  fromDashboard?: boolean;
}

const COUNTRY_ALIASES: Record<string, string> = {
  usa: "United States",
  "u.s.a.": "United States",
  "us": "United States",
  "u.s.": "United States",
  uk: "United Kingdom",
  "u.k.": "United Kingdom",
  // Add more as needed
};

// Utility to clean strings: trims and removes leading/trailing quotes
const cleanString = (value: any) =>
  typeof value === "string"
    ? value.trim().replace(/^"+|"+$/g, "")
    : value;

const GeneralReleaseInfo: React.FC = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext<PitchFormValues>();
  const location = useLocation();
  const state = location.state as LocationState;

  const [lastSelectedArtist, setLastSelectedArtist] = useState<string>("");
  const [artists, setArtists] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredArtists, setFilteredArtists] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [artistIdforAssets, setArtistIdforAssets] = useState<string>("");
  const configOptions = ["Single", "Album", "EP", "Waterfall"];
  const [pitchImages, setPitchImages] = useState<ImageWithArtistId[]>([]);
  const [uploadedImages, setUploadedImages] = useState<{ url: string; imageTitle: string }[]>([]);
  const [selectedImages, setSelectedImages] = useState<{ url: string; imageTitle: string }[]>([]);
  // Auto-populate form fields when artist is selected from typeahead
  const handleArtistSelection = async (artistName: string, artistData?: any) => {
    setArtistIdforAssets(artistData?.id);
    setFieldValue("artistName", artistName);

    if (artistData && artistName === lastSelectedArtist) return;
    if (artistData) {
      setLastSelectedArtist(artistName);

      if (artistData.spotifyURI && !values.spotifyUri) {
        setFieldValue("spotifyUri", cleanString(artistData.spotifyURI));
      }
      if (artistData.asin && !values.asin) {
        setFieldValue("asin", cleanString(artistData.asin));
      }
      if (artistData.appleID && !values.appleId) {
        setFieldValue("appleId", cleanString(artistData.appleID));
      }
      if (artistData.hometown && !values.artistHometown) {
        setFieldValue("artistHometown", cleanString(artistData.hometown));
      }
      if (artistData.currentTown && !values.artistCurrentTown) {
        setFieldValue("artistCurrentTown", cleanString(artistData.currentTown));
      }
      if (artistData.country) {
        // Clean and normalize input
        const normalized = cleanString(artistData.country).toLowerCase();
        // Check for alias
        const mappedCountry = COUNTRY_ALIASES[normalized] || cleanString(artistData.country);

        // Try to find a match in the dropdown options (case-insensitive)
        const matchedCountry = dspDropdownOptions.artistCountry.find(
          (c) => c.trim().toLowerCase() === mappedCountry.trim().toLowerCase()
        );
        if (matchedCountry) {
          setFieldValue("artistCountry", matchedCountry);
        } else {
          setFieldValue("artistCountry", "");
        }
      } else {
        setFieldValue("artistCountry", ""); // or leave as is
      }
      if (artistData.distributor && !values.distributor) {
        setFieldValue("distributor", cleanString(artistData.distributor));
      }
      if (artistData.tidalURL && !values.TIDALpage) {
        setFieldValue("TIDALpage", cleanString(artistData.tidalURL));
      }
      if (artistData.soundcloudURL && !values.soundCloudProfileUrl) {
        setFieldValue("soundCloudProfileUrl", cleanString(artistData.soundcloudURL));
      }

      const socialLinks = [
        artistData.instagram && `Instagram: ${cleanString(artistData.instagram)}`,
        artistData.tiktok && `TikTok: ${cleanString(artistData.tiktok)}`,
        artistData.twitter && `Twitter: ${cleanString(artistData.twitter)}`,
        artistData.facebook && `Facebook: ${cleanString(artistData.facebook)}`,
        artistData.snapchat && `Snapchat: ${cleanString(artistData.snapchat)}`,
      ].filter(Boolean).join(', ');

      if (socialLinks && !values.socialsLinks) {
        setFieldValue("socialsLinks", socialLinks);
      }

      Swal.fire({
        title: 'Artist Data Loaded',
        text: `Form has been populated with ${artistName}'s information from your roster.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    } else {
      setLastSelectedArtist("");
    }
  };

  const mergeAndSetImages = (uploadArr: { url: string; imageTitle: string }[], selectArr: { url: string; imageTitle: string }[]) => {
    const merged = [...uploadArr, ...selectArr].filter(
      (item, idx, arr) => arr.findIndex(a => a.url === item.url) === idx
    );

    // Add artistId to each image object
    const mergedWithArtistId = merged.map(image => ({
      ...image,
      artistId: artistIdforAssets
    }));

    setPitchImages(mergedWithArtistId);
    setFieldValue("images", mergedWithArtistId);
  };

  const handleUploadSuccess = (newAssets: { url: string; imageTitle: string }[]) => {
    setUploadedImages(newAssets);
    mergeAndSetImages(newAssets, selectedImages);
  };

  const syncSelectedAssets = (assets: { url: string; imageTitle: string }[]) => {
    setSelectedImages(assets);
    // Merge uploaded images (from new uploads) with selected assets (from existing assets)
    const allImages = [...uploadedImages, ...assets].filter(
      (item, idx, arr) => arr.findIndex(a => a.url === item.url) === idx
    );

    // Add artistId to each image object
    const mergedWithArtistId = allImages.map(image => ({
      ...image,
      artistId: artistIdforAssets
    }));

    setPitchImages(mergedWithArtistId);
    setFieldValue("images", mergedWithArtistId);
  };

  const handleRemoveImage = (indexToRemove: number) => {
    const imageToRemove = pitchImages[indexToRemove];
    const updatedImages = pitchImages.filter((_, index) => index !== indexToRemove);

    // Update local states
    setPitchImages(updatedImages);
    setFieldValue("images", updatedImages);

    // Update selected images state (remove from both uploaded and selected)
    setUploadedImages(prev => prev.filter(img => img.url !== imageToRemove.url));
    setSelectedImages(prev => prev.filter(img => img.url !== imageToRemove.url));
  };


  // Check for new Spotify URI not in roster
  const checkSpotifyURI = async (spotifyUri: string) => {
    if (!spotifyUri || !spotifyUri.includes('spotify:artist:') && !spotifyUri.match(/^[0-9A-Za-z]{22}$/)) {
      return;
    }

    try {
      const artistId = spotifyUri.includes('spotify:artist:')
        ? spotifyUri.split('spotify:artist:')[1]
        : spotifyUri;

      const response = await artistsApi.checkSpotifyURI(artistId);

      if (!response.data) {
        const result = await Swal.fire({
          title: 'New Artist Detected',
          html: `
            <p>This Spotify URI is not in your roster.</p>
            <p><strong>Would you like to add this artist to your roster?</strong></p>
            <p class="text-sm text-gray-600 mt-2">This will help auto-fill future pitches for this artist.</p>
          `,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes, Add to Roster',
          cancelButtonText: 'No, Continue',
          confirmButtonColor: '#3B82F6',
          cancelButtonColor: '#6B7280'
        });

        if (result.isConfirmed) {

          // Prepare data for new artist
          const newArtistData = {
            artistName: values.artistName,
            spotifyURI: spotifyUri,
            asin: values.asin || 'TBD',
            appleID: values.appleId || '3343535',
            hometown: values.artistHometown || '',
            currentTown: values.artistCurrentTown || '',
            country: values.artistCountry || '',
            distributor: values.distributor || '',
            tidalURL: values.TIDALpage || 'https://tidal.com/browse/artist/tbd',
            soundcloudURL: values.soundCloudProfileUrl || 'https://soundcloud.com/tbd',
            // Parse social links if available
            instagram: '',
            tiktok: '',
            twitter: '',
            facebook: '',
            snapchat: ''
          };

          try {
            await artistsApi.create(newArtistData);

            Swal.fire({
              title: 'Artist Added!',
              text: `${values.artistName} has been added to your roster.`,
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          } catch (error) {
            console.error('Error adding artist to roster:', error);
            Swal.fire({
              title: 'Error',
              text: 'Failed to add artist to roster. Please try again.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        }
      }
    } catch (error) {
      console.error('Error checking Spotify URI:', error);
      // Silently fail - don't interrupt user flow
    }
  };

  // Monitor Spotify URI changes
  useEffect(() => {
    if (values.spotifyUri && values.artistName) {
      const timeoutId = setTimeout(() => {
        checkSpotifyURI(values.spotifyUri as string);
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(timeoutId);
    }
  }, [values.spotifyUri, values.artistName]);

  useEffect(() => {
    const loadArtists = async () => {
      try {
        const response = await artistsApi.getAll({ limit: 100 });
        const artistsData = response.data.data.artists || [];
        setArtists(artistsData);
      } catch (error) {
        console.error('Error loading artists:', error);
      }
    };
    loadArtists();
  }, []);

  // Set artistId from existing images when form loads
  useEffect(() => {
    if (values.images && Array.isArray(values.images) && values.images.length > 0) {
      const firstImage = values.images[0] as ImageWithArtistId;
      if (firstImage.artistId && !artistIdforAssets) {
        setArtistIdforAssets(String(firstImage.artistId));
        setPitchImages(values.images as ImageWithArtistId[]);
      }
    }
  }, [values.images, artistIdforAssets]);

  const handleArtistInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFieldValue("artistName", value);

    if (value.length > 0) {
      const filtered = artists.filter(artist =>
        artist.artistName.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredArtists(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredArtists([]);
      setShowSuggestions(false);
    }
  };
  // Add this useEffect to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event:any) => {
      if (isDropdownOpen && !event.target.closest('.promo-photos-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  return (
    <section className="p-6 bg-white rounded-lg shadow-sm">
      <h2 className="mb-6 text-xl font-semibold text-gray-900">General Release Info</h2>

      {/* Only show this row if coming from top "Create Pitch" button */}
      {!state?.fromDashboard && (
        <div className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <label
              htmlFor="artist-name"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Artist Name <RequiredAsterisk />

            </label>
            <ArtistTypeahead
              value={values.artistName || ""}
              onChange={handleArtistSelection}
              placeholder="Enter Artist Name"
              error={errors.artistName}
              touched={touched.artistName}
            />
            <ErrorMessage name="artistName" component="div" className="text-sm text-red-500" />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="release-title"
              className="block text-sm font-medium text-gray-900"
            >
              Release Title     <RequiredAsterisk />
            </label>
            <input
              type="text"
              id="release-title"
              name="releaseTitle"
              placeholder="Enter Release Title"
              value={values.releaseTitle || ""}
              onChange={(e) => setFieldValue("releaseTitle", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.releaseTitle && touched.releaseTitle ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <ErrorMessage name="releaseTitle" component="div" className="text-sm text-red-500" />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="release-date"
              className="block text-sm font-medium text-gray-900"
            >
              Release Date <RequiredAsterisk />
            </label>
            <input
              type="date"
              id="release-date"
              name="releaseDate"
              value={values.releaseDate || ""}
              onChange={(e) => setFieldValue("releaseDate", e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.releaseDate && touched.releaseDate ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <ErrorMessage name="releaseDate" component="div" className="text-sm text-red-500" />
          </div>
        </div>
      )}

      {/* Rest of the component remains the same */}
      {/* Core Release Information */}
      <div className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <label
            htmlFor="configuration"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Configuration <RequiredAsterisk />
          </label>
          <Select
            value={values.configuration}
            onValueChange={(value) => {
              setFieldValue("configuration", value);
              if (value === "Single") {
                setFieldValue("focusTrackName", "");
              }
            }}
          >
            <SelectTrigger className={`w-full ${errors.configuration && touched.configuration ? 'border-red-500' : ''
              }`}>
              <SelectValue placeholder="Select Configuration" />
            </SelectTrigger>
            <SelectContent>
              {configOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ErrorMessage name="configuration" component="div" className="text-sm text-red-500" />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="release-upc"
            className="block text-sm font-medium text-gray-900"
          >
            Release UPC  <RequiredAsterisk />
          </label>
          <input
            type="text"
            id="release-upc"
            name="releaseUpc"
            placeholder="Enter Release UPC"
            value={values.releaseUpc || ""}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 12);
              setFieldValue("releaseUpc", value);
            }}
            maxLength={12}
            className={`w-full px-3 py-2 text-sm border rounded-md  ${errors.releaseUpc && touched.releaseUpc ? 'border-red-500' : 'border-gray-300'
              }`}
          />
          <ErrorMessage name="releaseUpc" component="div" className="text-sm text-red-500" />
        </div>

        {(values.configuration === "EP" || values.configuration === "Album") && (
          <div className="space-y-2">
            <label
              htmlFor="focus-track"
              className="block text-sm font-medium text-gray-900"
            >
              Focus Track <RequiredAsterisk />
            </label>
            <input
              type="text"
              id="focus-track"
              name="focusTrackName"
              placeholder="Enter Focus Track Name"
              value={values.focusTrackName || ""}
              onChange={(e) => setFieldValue("focusTrackName", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.focusTrackName && touched.focusTrackName ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <ErrorMessage name="focusTrackName" component="div" className="text-sm text-red-500" />
          </div>
        )}

        <div className="space-y-2">
          <label
            htmlFor="track-isrc"
            className="block text-sm font-medium text-gray-900"
          >
            Track ISRC  <RequiredAsterisk />
          </label>
          <input
            type="text"
            id="track-isrc"
            name="trackIsrc"
            placeholder="Enter Track ISRC"
            value={values.trackIsrc || ""}
            onChange={(e) => setFieldValue("trackIsrc", e.target.value)}
            maxLength={20}
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.trackIsrc && touched.trackIsrc ? 'border-red-500' : 'border-gray-300'
              }`}
          />
          <ErrorMessage name="trackIsrc" component="div" className="text-sm text-red-500" />
        </div>
      </div>

      {/* Featured Artists and Alt Titles */}
      <div className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <label
            htmlFor="featured-artists"
            className="block text-sm font-medium text-gray-900"
          >
            Featured Artists
          </label>
          <input
            type="text"
            id="featured-artists"
            name="featuredArtists"
            placeholder="Enter Featured Artists"
            value={values.featuredArtists || ""}
            onChange={(e) => setFieldValue("featuredArtists", e.target.value)}
            className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="artist-uri"
            className="block text-sm font-medium text-gray-900"
          >
            Featured Artist URI
          </label>
          <input
            type="text"
            id="artist-uri"
            name="featuredArtistUri"
            placeholder="Enter URI"
            value={values.featuredArtistUri || ""}
            onChange={(e) => setFieldValue("featuredArtistUri", e.target.value)}
            className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="alt-titles"
            className="block text-sm font-medium text-gray-900"
          >
            Alt Titles
          </label>
          <input
            type="text"
            id="alt-titles"
            name="altTitles"
            placeholder="Ex. Acoustic, Live, Spotify Sessions"
            value={values.altTitles || ""}
            onChange={(e) => setFieldValue("altTitles", e.target.value)}
            className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Description Fields */}

      <div className="mb-6 ">
        <div className="space-y-2">
          <label
            htmlFor="short-description"
            className="block text-sm font-medium text-gray-900"
          >
            Description <RequiredAsterisk />
            {/* <InfoTooltip content="Include compelling recent achievements (sync placements, viral social content, major collaborations), artist quotes, and significant marketing drivers to capture editorial attention." /> */}
          </label>
          <div className="relative">
            <textarea
              id="short-description"
              name="shortDescription"
              placeholder="Description..."
              value={values.shortDescription || ""}
              onChange={(e) => {
                const characterCount = e.target.value.length;
                if (characterCount <= 500) {
                  setFieldValue("shortDescription", e.target.value);
                }
              }}
              maxLength={500}
              rows={5}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.shortDescription && touched.shortDescription ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {/* Character Counter for Description */}
            <div className="absolute bottom-2 right-3">

              <CharacterCounterDisplay
                value={values.shortDescription || ""}
                maxLength={500}

              />
            </div>
          </div>
          <ErrorMessage name="shortDescription" component="div" className="text-sm text-red-500" />
          <p className="text-sm text-gray-500 ">
            Provide a compelling overview that combines the song's essence with its commercial appeal.
            Include an artist quote about the song's creation or meaning, musical context (genre, sonic comparisons, themes),
            and any momentum indicators that demonstrate why this track deserves editorial attention.
          </p>
        </div>
      </div>


      <div className="mb-6">
        <div className="space-y-2">
          <label
            htmlFor="long-description"
            className="block text-sm font-medium text-gray-900"
          >
            Marketing Drivers <RequiredAsterisk />

          </label>
          <div className="relative">
            <textarea
              id="long-description"
              name="longDescription"
              placeholder=" Describe the primary promotional activities and strategic initiatives actively supporting this release..."
              value={values.longDescription || ""}
              onChange={(e) => {
                const characterCount = e.target.value.length;
                if (characterCount <= 500) {
                  setFieldValue("longDescription", e.target.value);
                }
              }}
              rows={5}
              maxLength={500}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.longDescription && touched.longDescription ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {/* Character Counter for Marketing Drivers */}
            <div className="absolute bottom-2 right-3">
              <CharacterCounterDisplay
                value={values.longDescription || ""}
                maxLength={500}
              />
            </div>
          </div>
          <ErrorMessage name="longDescription" component="div" className="text-sm text-red-500" />
          <p className="mt-2 text-sm text-gray-500">
            Describe the primary promotional activities and strategic initiatives actively
            supporting this release. Include concrete marketing efforts, partnerships, and momentum-building
            activities that demonstrate industry investment and audience engagement potential.
          </p>
        </div>
      </div>

      <div className="mb-6">
        <div className="space-y-2">
          <label
            htmlFor="long-description"
            className="block text-sm font-medium text-gray-900"
          >
            Lyrics
          </label>
          <textarea
            id="lyrics"
            name="lyrics"
            placeholder="Enter Lyrics"
            value={values.lyrics || ""}
            onChange={(e) => setFieldValue("lyrics", e.target.value)}
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.lyrics && touched.lyrics ? 'border-red-500' : 'border-gray-300'
              }`}
          />
          <ErrorMessage name="lyrics" component="div" className="text-sm text-red-500" />
        </div>
      </div>

      {/* Multi-select Dropdowns */}
      <div className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <label
            htmlFor="song-description"
            className="block text-sm font-medium text-gray-900"
          >
            Describe the Song <RequiredAsterisk />
          </label>
          <MultiSelect
            options={dspDropdownOptions.songDescription}
            value={values.songDescription || []}
            onChange={(newValues) => setFieldValue("songDescription", newValues)}
            placeholder="Select Description"
          />
          <ErrorMessage name="songDescription" component="div" className="text-sm text-red-500" />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="main-genre"
            className="block text-sm font-medium text-gray-900"
          >
            Main Genre <RequiredAsterisk />
          </label>
          <MultiSelect
            options={dspDropdownOptions.mainGenre}
            value={values.mainGenre || []}
            onChange={(newValues) => setFieldValue("mainGenre", newValues)}
            placeholder="Select Genre"
            maxSelections={3}
          />
          <ErrorMessage name="mainGenre" component="div" className="text-sm text-red-500" />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="song-mood"
            className="block text-sm font-medium text-gray-900"
          >
            Song Mood  <RequiredAsterisk />
          </label>
          <MultiSelect
            options={dspDropdownOptions.songMood}
            value={values.songMood || []}
            onChange={(newValues) => setFieldValue("songMood", newValues)}
            placeholder="Select Mood"
            maxSelections={3}
          />
          <ErrorMessage name="songMood" component="div" className="text-sm text-red-500" />
        </div>
      </div>

      {/* Links and Media */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <label
            htmlFor="language"
            className="block text-sm font-medium text-gray-900"
          >
            Language <RequiredAsterisk />
          </label>
          <MultiSelect
            options={dspDropdownOptions.language}
            value={values.language || []}
            onChange={(newValues) => setFieldValue("language", newValues)}
            placeholder="Select Language"
          />
          <ErrorMessage name="language" component="div" className="text-sm text-red-500" />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="link-to-listen"
            className="block text-sm font-medium text-gray-900"
          >
            Link to Listen  <RequiredAsterisk />
            <InfoTooltip content="Provide a publicly accessible streaming link. Disco links are preferred, but any
link that doesn't require login credentials will work" />
          </label>
          <input
            type="url"
            id="link-to-listen"
            name="linkToListen"
            placeholder="Enter Link"
            value={values.linkToListen || ""}
            onChange={(e) => setFieldValue("linkToListen", e.target.value)}
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.linkToListen && touched.linkToListen ? 'border-red-500' : 'border-gray-300'
              }`}
          />
          <ErrorMessage name="linkToListen" component="div" className="text-sm text-red-500" />
        </div>

        <div className="space-y-2 relative promo-photos-dropdown">
          <label className="block text-sm font-medium text-gray-900">
            Promo Photos / Images <RequiredAsterisk />
            <InfoTooltip content="Share high-quality promotional images via a public Google Drive link for editorial teams to use in playlist features and social promotion, or upload images directly." />
          </label>

          {/* Dropdown Toggle */}
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <span className="text-gray-700">Select option...</span>
            <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Content */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 z-10 border border-gray-300 rounded-md p-4 bg-white shadow-lg mt-1">
              {/* URL Input Section */}
              <div className="space-y-2 mb-4">
                <label
                  htmlFor="promo-photos"
                  className="block text-xs font-medium text-gray-700"
                >
                  Link to Photos
                </label>
                <input
                  type="url"
                  id="promo-photos"
                  name="promoPhotos"
                  placeholder="Enter Link to Photos"
                  value={values.promoPhotos || ""}
                  onChange={(e) => setFieldValue("promoPhotos", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.promoPhotos && touched.promoPhotos ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                <ErrorMessage name="promoPhotos" component="div" className="text-sm text-red-500" />
              </div>

              {/* Divider */}
              <div className="flex items-center my-3">
                <div className="flex-1 border-t border-gray-300"></div>
              
                  
              </div>

              {/* Upload Section */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-700">
                  Upload Images
                </label>
                <button
                  type="button"
                  onClick={() => artistIdforAssets && setIsModalOpen(true)}
                  disabled={!artistIdforAssets}
                  className={`flex items-center w-full px-4 py-2 rounded-lg border 
              ${!artistIdforAssets
                      ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-white border-gray-300 text-gray-700 hover:border-indigo-500 hover:shadow focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    } transition-all duration-200`}
                  style={{ minHeight: "48px" }}
                >
                  <ArrowUpTrayIcon className={`w-5 h-5 mr-3 ${artistIdforAssets ? "text-indigo-500" : "text-gray-300"}`} />
                  <span className="flex-1 text-sm font-medium text-left">
                    Upload Images
                  </span>
                </button>
              </div>
            </div>
          )}

          <UploadModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            artistId={artistIdforAssets}
            onUploadSuccess={handleUploadSuccess}
            onSelectionChange={syncSelectedAssets}
            maxTotalImages={5}
            showCheckboxes={true}
            initialSelectedImages={pitchImages.map(img => ({ url: img.url, imageTitle: img.imageTitle }))}
          />
        </div>
      </div>

    

      <div className="flex flex-wrap gap-3 mt-5">
        {pitchImages.map((img, idx) => (
          <div
            key={idx}
            className="relative flex flex-col bg-white border border-gray-200 rounded-lg shadow-sm"
            style={{ width: '120px' }}
          >
            {/* Delete button */}
            <button
              type="button"
              onClick={() => handleRemoveImage(idx)}
              className="absolute z-10 flex items-center justify-center w-6 h-6 text-white transition-colors bg-red-500 rounded-full shadow-lg -top-2 -right-2 hover:bg-red-600"
            >
              <XMarkIcon className="w-3 h-3" />
            </button>

            {/* Image container */}
            <div className="relative w-full h-20 overflow-hidden rounded-t-lg bg-gray-100">
              <img
                src={img.url}
                alt={img.imageTitle}
                className="object-cover w-full h-full"
              />
            </div>

            {/* Title */}
            <div className="p-2">
              <span className="block text-xs font-medium text-gray-700 truncate">
                {img.imageTitle}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default GeneralReleaseInfo;